import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { Button, Card, Skeleton, Input } from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Pagination,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { paginationView } from '@/pages/customers/pagination-summary';

const PAGE_SIZE = 20;

/** Debounce a fast-changing value (search box) to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Minimal list envelope the masters hooks return. */
interface ListResult<Row> {
  results: Row[];
  pagination?: { total?: number; total_pages?: number };
}

export interface MastersListSectionProps<Row> {
  /** Data hook, called with server `{ page, page_size, search }`. */
  useList: (params: { page: number; page_size: number; search?: string }) => {
    data?: ListResult<Row>;
    isLoading: boolean;
    isError: boolean;
    isFetching: boolean;
    refetch: () => void;
  };
  columns: ColumnDef<Row, unknown>[];
  getRowId: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  searchPlaceholder: string;
  emptyIcon: ReactNode;
  emptyTitle: string;
  errorTitle: string;
}

/**
 * Server-paginated masters list: a search `Toolbar` over a `DataTable` with a
 * `Pagination` footer. Search (`search`) and page are pushed to the backend via
 * the injected `useList` hook; loading → Skeleton, error → Alert+retry, empty →
 * EmptyState. Read-only — no row selection, merge, or mutations (W1 scope).
 * Shared by the Suppliers, Models and Locations lists.
 */
export function MastersListSection<Row>({
  useList,
  columns,
  getRowId,
  onRowClick,
  searchPlaceholder,
  emptyIcon,
  emptyTitle,
  errorTitle,
}: MastersListSectionProps<Row>) {
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);

  const params = {
    page,
    page_size: PAGE_SIZE,
    search: query || undefined,
  };
  const { data, isLoading, isError, isFetching, refetch } = useList(params);

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const footer = useMemo(() => {
    const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
    return (
      <Pagination
        page={page}
        pageCount={view.pageCount}
        onPageChange={setPage}
        summary={`${view.summary}${isFetching ? ' …' : ''}`}
      />
    );
  }, [page, rows.length, total, pageCount, isFetching]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={rawQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={searchPlaceholder}
            startSlot={<Search />}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title={errorTitle}
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={getRowId}
          onRowClick={onRowClick}
          emptyState={
            <EmptyState
              icon={emptyIcon}
              title={emptyTitle}
              description="Try adjusting your search."
            />
          }
          footer={footer}
        />
      )}
    </div>
  );
}
